// CoPilot provenance set-math (CR-20260619-001; FR-75..84, FR-97/100/107).
//
// This is the PURE logic for "which working-state legs did CoPilot author, and what must
// un-Preview remove". The actual grid wiring (mapping these refs to real HedgePositionModel
// instances, splicing them out via removeLeg/resetGroup) is M3 (the facade). The SET LOGIC
// lives here and is unit-tested directly (TC-U-F07/F08/F09/F10).
//
// Legs are tracked by opaque reference T (the grid uses HedgePositionModel by identity; the
// pure specs use strings/objects). Membership is by reference (Set<T>) — never by value/Name.
//
// ES5 note: NO for..of / spread over Set/Map (downlevelIteration is off). Use forEach /
// Array.from only (project rule).

import { DivergenceState } from './copilot-domain';

export type LegAuthor = 'copilot' | 'user';

export class CopilotProvenanceLedger<T> {
    // solutionId -> the set of leg refs CoPilot added for that Solution's Preview.
    private readonly _addedBySolution = new Map<string, Set<T>>();

    // Record legs CoPilot just placed for a Solution's Preview (additive; FR-75/80).
    recordAdded(solutionId: string, legs: T[]): void {
        let set = this._addedBySolution.get(solutionId);
        if (!set) {
            set = new Set<T>();
            this._addedBySolution.set(solutionId, set);
        }
        legs.forEach(leg => set.add(leg));
    }

    // The CoPilot legs currently attributed to a Solution.
    legsFor(solutionId: string): T[] {
        const set = this._addedBySolution.get(solutionId);
        return set ? Array.from(set) : [];
    }

    // un-Preview a Solution (FR-77/79/82/145; TC-U-F07): returns EXACTLY that Solution's
    // CoPilot legs (to be spliced out by the caller) and forgets them. User legs and other
    // Solutions' legs are never returned.
    removeSolution(solutionId: string): T[] {
        const legs = this.legsFor(solutionId);
        this._addedBySolution.delete(solutionId);
        return legs;
    }

    // Within-tab REPLACE (FR-80/86; TC-U-F08): the PURE set-math model — remove the tab's prior Solution
    // legs, then add the new ones. NOTE: production within-tab replace (M3-4) is done in the orchestrator as
    // removeCopilotLegs(prior) + applySolution(new) — two facade calls, atomicity-safer than this combined
    // form (removal commits before the failable apply). This helper stays as the spec-verified set model.
    // Cross-tab UNION is simply independent recordAdded calls (no removal) — additive by design.
    replaceWithin(priorSolutionId: string, newSolutionId: string, newLegs: T[]): T[] {
        const removed = this.removeSolution(priorSolutionId);
        this.recordAdded(newSolutionId, newLegs);
        return removed;
    }

    // Is this leg ref CoPilot-authored (any Solution)? (provenance tag; FR-76)
    isCopilotLeg(leg: T): boolean {
        let found = false;
        this._addedBySolution.forEach(set => {
            if (set.has(leg)) {
                found = true;
            }
        });
        return found;
    }

    // All CoPilot-authored leg refs across all Solutions (de-duplicated by Set semantics).
    allCopilotLegs(): T[] {
        const out: T[] = [];
        this._addedBySolution.forEach(set => set.forEach(leg => out.push(leg)));
        return out;
    }

    solutionIds(): string[] {
        return Array.from(this._addedBySolution.keys());
    }

    clear(): void {
        this._addedBySolution.clear();
    }
}

// Divergence (FR-97/100/107; TC-U-F10): the Conversation is 'custom' iff a user-authored leg
// sits ATOP CoPilot legs — i.e. CoPilot legs are present AND >=1 user-authored leg exists.
// 'armed' otherwise (only-CoPilot, or no CoPilot legs at all). Recomputed from the CURRENT
// leg authors after each edit / re-run; it never latches.
export function computeDivergence(legAuthors: LegAuthor[]): DivergenceState {
    let copilot = 0;
    let user = 0;
    legAuthors.forEach(a => {
        if (a === 'copilot') {
            copilot++;
        } else if (a === 'user') {
            user++;
        }
    });
    return copilot >= 1 && user >= 1 ? 'custom' : 'armed';
}
